import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert
} from 'reactstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { customDropdownWithBorder } from '../../util/reactCustomStyles';
import { shouldShowCodes, getDisplayName } from '../../util/specialtyDisplayHelper';
import {
  fetchSubjects,
  createSubject,
  updateSubject,
  deleteSubject,
  fetchUnits,
  createUnit,
  updateUnit,
  deleteUnit,
  fetchTopics,
  createTopic,
  updateTopic,
  deleteTopic
} from '../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchQuestions } from '../../redux/QuestionBank/QuestionsAction';

const ManageSubjectsUnits = ({ userEmail }) => {
  const dispatch = useDispatch();

  // Redux state
  const { subjects, loading: subjectsLoading } = useSelector(state => state.questionBankSubjects);
  const { units, loading: unitsLoading } = useSelector(state => state.questionBankUnits);
  const { topics, loading: topicsLoading } = useSelector(state => state.questionBankTopics);
  const { questions } = useSelector(state => state.questionBankQuestions);
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);
  const showCodes = shouldShowCodes(activeSpecialty);

  // Local state for subjects
  const [subjectModal, setSubjectModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [subjectForm, setSubjectForm] = useState({ code: '', name: '', description: '' });

  // Local state for units
  const [unitModal, setUnitModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({ subjectId: '', code: '', name: '', description: '' });

  // Local state for topics
  const [topicModal, setTopicModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [topicForm, setTopicForm] = useState({ unitId: '', subjectId: '', code: '', name: '', description: '' });

  // Selected subject for filtering units
  const [selectedSubjectId, setSelectedSubjectId] = useState('');

  // Selected unit for filtering topics
  const [selectedUnitId, setSelectedUnitId] = useState('');

  // Search state
  const [subjectSearch, setSubjectSearch] = useState('');
  const [unitSearch, setUnitSearch] = useState('');
  const [topicSearch, setTopicSearch] = useState('');

  // Pagination
  const [subjectPage, setSubjectPage] = useState(1);
  const [unitPage, setUnitPage] = useState(1);
  const [topicPage, setTopicPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchQuestions());
  }, [dispatch, activeSpecialty]);

  // Reset pagination when filters/search change
  useEffect(() => {
    setSubjectPage(1);
  }, [subjectSearch]);

  useEffect(() => {
    setUnitPage(1);
    setUnitSearch('');
  }, [selectedSubjectId]);

  useEffect(() => {
    setTopicPage(1);
    setTopicSearch('');
  }, [selectedUnitId]);

  // Helper functions for getting names (needed for search)
  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.subjectId === subjectId);
    return subject ? subject.name : 'Unknown';
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.unitId === unitId);
    return unit ? unit.name : 'Unknown';
  };

  // Check if a subject/unit/topic has questions linked to it
  const subjectHasQuestions = (subjectId) => questions?.some(q => q.subjectId === subjectId);
  const unitHasQuestions = (unitId) => questions?.some(q => q.unitId === unitId);
  const topicHasQuestions = (topicId) => questions?.some(q => q.topicId === topicId);

  // Filter and search subjects
  const searchedSubjects = React.useMemo(() => {
    if (!subjectSearch.trim()) return subjects;
    const searchLower = subjectSearch.toLowerCase().trim();
    return subjects.filter(subject =>
      (showCodes && subject.code?.toLowerCase().includes(searchLower)) ||
      subject.name?.toLowerCase().includes(searchLower) ||
      subject.description?.toLowerCase().includes(searchLower)
    );
  }, [subjects, subjectSearch, showCodes]);

  // Filter units by selected subject and search
  const filteredUnits = React.useMemo(() => {
    let result = selectedSubjectId
      ? units.filter(unit => unit.subjectId === selectedSubjectId)
      : units;

    if (unitSearch.trim()) {
      const searchLower = unitSearch.toLowerCase().trim();
      result = result.filter(unit =>
        (showCodes && unit.code?.toLowerCase().includes(searchLower)) ||
        unit.name?.toLowerCase().includes(searchLower) ||
        unit.description?.toLowerCase().includes(searchLower) ||
        getSubjectName(unit.subjectId)?.toLowerCase().includes(searchLower)
      );
    }
    return result;
  }, [units, selectedSubjectId, unitSearch, subjects]);

  // Filter topics by selected unit and search
  const filteredTopics = React.useMemo(() => {
    let result = selectedUnitId
      ? topics.filter(topic => topic.unitId === selectedUnitId)
      : topics;

    if (topicSearch.trim()) {
      const searchLower = topicSearch.toLowerCase().trim();
      result = result.filter(topic =>
        (showCodes && topic.code?.toLowerCase().includes(searchLower)) ||
        topic.name?.toLowerCase().includes(searchLower) ||
        topic.description?.toLowerCase().includes(searchLower) ||
        getUnitName(topic.unitId)?.toLowerCase().includes(searchLower) ||
        getSubjectName(topic.subjectId)?.toLowerCase().includes(searchLower)
      );
    }
    return result;
  }, [topics, selectedUnitId, topicSearch, units, subjects]);

  // Pagination calculations
  const paginatedSubjects = searchedSubjects.slice((subjectPage - 1) * itemsPerPage, subjectPage * itemsPerPage);
  const totalSubjectPages = Math.ceil(searchedSubjects.length / itemsPerPage);

  const paginatedUnits = filteredUnits.slice((unitPage - 1) * itemsPerPage, unitPage * itemsPerPage);
  const totalUnitPages = Math.ceil(filteredUnits.length / itemsPerPage);

  const paginatedTopics = filteredTopics.slice((topicPage - 1) * itemsPerPage, topicPage * itemsPerPage);
  const totalTopicPages = Math.ceil(filteredTopics.length / itemsPerPage);

  // ========== SUBJECT HANDLERS ==========

  const toggleSubjectModal = () => {
    setSubjectModal(!subjectModal);
    if (subjectModal) {
      setEditingSubject(null);
      setSubjectForm({ code: '', name: '', description: '' });
    }
  };

  const handleEditSubject = (subject) => {
    setEditingSubject(subject);
    setSubjectForm({ code: subject.code || '', name: subject.name, description: subject.description || '' });
    setSubjectModal(true);
  };

  const handleSubjectSubmit = async (e) => {
    e.preventDefault();

    if (showCodes && !subjectForm.code.trim()) {
      Swal.fire('Error', 'Subject code is required', 'error');
      return;
    }

    if (!subjectForm.name.trim()) {
      Swal.fire('Error', 'Subject name is required', 'error');
      return;
    }

    // Check for duplicate subject code (PMR only)
    if (showCodes) {
      const duplicateCode = subjects.find(
        s => s.code?.toLowerCase().trim() === subjectForm.code.toLowerCase().trim()
          && (!editingSubject || s.subjectId !== editingSubject.subjectId)
      );
      if (duplicateCode) {
        Swal.fire('Error', `A subject with code "${duplicateCode.code}" already exists.`, 'error');
        return;
      }
    }

    // M7: Check for duplicate subject name
    const duplicateSubject = subjects.find(
      s => s.name.toLowerCase().trim() === subjectForm.name.toLowerCase().trim()
        && (!editingSubject || s.subjectId !== editingSubject.subjectId)
    );
    if (duplicateSubject) {
      Swal.fire('Error', `A subject named "${duplicateSubject.name}" already exists.`, 'error');
      return;
    }

    let result;
    if (editingSubject) {
      result = await dispatch(updateSubject(editingSubject.subjectId, subjectForm));
    } else {
      result = await dispatch(createSubject(subjectForm, userEmail));
    }

    if (result.success) {
      Swal.fire('Success', `Subject ${editingSubject ? 'updated' : 'created'} successfully`, 'success');
      toggleSubjectModal();
      dispatch(fetchSubjects());
    } else {
      Swal.fire('Error', result.error, 'error');
    }
  };

  const handleDeleteSubject = async (subjectId, subjectName) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete subject "${subjectName}"? This will fail if there are units or questions linked to it.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (confirm.isConfirmed) {
      const result = await dispatch(deleteSubject(subjectId));
      if (result.success) {
        Swal.fire('Deleted!', 'Subject has been deleted.', 'success');
        dispatch(fetchSubjects());
        dispatch(fetchUnits());
      } else {
        Swal.fire('Error', result.error, 'error');
      }
    }
  };

  // ========== UNIT HANDLERS ==========

  const toggleUnitModal = () => {
    setUnitModal(!unitModal);
    if (unitModal) {
      setEditingUnit(null);
      setUnitForm({ subjectId: '', code: '', name: '', description: '' });
    }
  };

  const handleEditUnit = (unit) => {
    setEditingUnit(unit);
    setUnitForm({
      subjectId: unit.subjectId,
      code: unit.code || '',
      name: unit.name,
      description: unit.description || ''
    });
    setUnitModal(true);
  };

  const handleUnitSubmit = async (e) => {
    e.preventDefault();

    if (showCodes && !unitForm.code.trim()) {
      Swal.fire('Error', 'Unit code is required', 'error');
      return;
    }

    if (!unitForm.name.trim()) {
      Swal.fire('Error', 'Unit name is required', 'error');
      return;
    }

    if (!unitForm.subjectId) {
      Swal.fire('Error', 'Please select a subject', 'error');
      return;
    }

    // Check for duplicate unit code within the same subject (PMR only)
    if (showCodes) {
      const duplicateCode = units.find(
        u => u.subjectId === unitForm.subjectId
          && u.code?.toLowerCase().trim() === unitForm.code.toLowerCase().trim()
          && (!editingUnit || u.unitId !== editingUnit.unitId)
      );
      if (duplicateCode) {
        Swal.fire('Error', `A unit with code "${duplicateCode.code}" already exists in this subject.`, 'error');
        return;
      }
    }

    // M7: Check for duplicate unit name within the same subject
    const duplicateUnit = units.find(
      u => u.subjectId === unitForm.subjectId
        && u.name.toLowerCase().trim() === unitForm.name.toLowerCase().trim()
        && (!editingUnit || u.unitId !== editingUnit.unitId)
    );
    if (duplicateUnit) {
      Swal.fire('Error', `A unit named "${duplicateUnit.name}" already exists in this subject.`, 'error');
      return;
    }

    let result;
    if (editingUnit) {
      result = await dispatch(updateUnit(editingUnit.unitId, unitForm));
    } else {
      result = await dispatch(createUnit(unitForm, userEmail));
    }

    if (result.success) {
      Swal.fire('Success', `Unit ${editingUnit ? 'updated' : 'created'} successfully`, 'success');
      toggleUnitModal();
      dispatch(fetchUnits());
    } else {
      Swal.fire('Error', result.error, 'error');
    }
  };

  const handleDeleteUnit = async (unitId, unitName) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete unit "${unitName}"? This will fail if there are questions linked to it.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (confirm.isConfirmed) {
      const result = await dispatch(deleteUnit(unitId));
      if (result.success) {
        Swal.fire('Deleted!', 'Unit has been deleted.', 'success');
        dispatch(fetchUnits());
      } else {
        Swal.fire('Error', result.error, 'error');
      }
    }
  };

  // ========== TOPIC HANDLERS ==========

  const toggleTopicModal = () => {
    setTopicModal(!topicModal);
    if (topicModal) {
      setEditingTopic(null);
      setTopicForm({ unitId: '', subjectId: '', code: '', name: '', description: '' });
    }
  };

  const handleEditTopic = (topic) => {
    setEditingTopic(topic);
    setTopicForm({
      unitId: topic.unitId,
      subjectId: topic.subjectId,
      code: topic.code || '',
      name: topic.name,
      description: topic.description || ''
    });
    setTopicModal(true);
  };

  const handleTopicSubmit = async (e) => {
    e.preventDefault();

    if (showCodes && !topicForm.code.trim()) {
      Swal.fire('Error', 'Topic code is required', 'error');
      return;
    }

    if (!topicForm.name.trim()) {
      Swal.fire('Error', 'Topic name is required', 'error');
      return;
    }

    if (!topicForm.unitId) {
      Swal.fire('Error', 'Please select a unit', 'error');
      return;
    }

    // Check for duplicate topic code within the same unit (PMR only)
    if (showCodes) {
      const duplicateCode = topics.find(
        t => t.unitId === topicForm.unitId
          && t.code?.toLowerCase().trim() === topicForm.code.toLowerCase().trim()
          && (!editingTopic || t.topicId !== editingTopic.topicId)
      );
      if (duplicateCode) {
        Swal.fire('Error', `A topic with code "${duplicateCode.code}" already exists in this unit.`, 'error');
        return;
      }
    }

    // M7: Check for duplicate topic name within the same unit
    const duplicateTopic = topics.find(
      t => t.unitId === topicForm.unitId
        && t.name.toLowerCase().trim() === topicForm.name.toLowerCase().trim()
        && (!editingTopic || t.topicId !== editingTopic.topicId)
    );
    if (duplicateTopic) {
      Swal.fire('Error', `A topic named "${duplicateTopic.name}" already exists in this unit.`, 'error');
      return;
    }

    let result;
    if (editingTopic) {
      result = await dispatch(updateTopic(editingTopic.topicId, topicForm));
    } else {
      result = await dispatch(createTopic(topicForm, userEmail));
    }

    if (result.success) {
      Swal.fire('Success', `Topic ${editingTopic ? 'updated' : 'created'} successfully`, 'success');
      toggleTopicModal();
      dispatch(fetchTopics());
    } else {
      Swal.fire('Error', result.error, 'error');
    }
  };

  const handleDeleteTopic = async (topicId, topicName) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete topic "${topicName}"? This will fail if there are questions linked to it.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (confirm.isConfirmed) {
      const result = await dispatch(deleteTopic(topicId));
      if (result.success) {
        Swal.fire('Deleted!', 'Topic has been deleted.', 'success');
        dispatch(fetchTopics());
      } else {
        Swal.fire('Error', result.error, 'error');
      }
    }
  };

  return (
    <div>
      <Row>
        {/* Subjects Section */}
        <Col lg="12">
          <Card className="shadow mb-4">
            <CardHeader className="bg-transparent">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">Manage Subjects</h3>
                </Col>
                <Col className="text-right">
                  <Button color="primary" size="sm" onClick={toggleSubjectModal}>
                    <i className="fas fa-plus"></i> Add Subject
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {subjectsLoading ? (
                <div className="text-center">Loading subjects...</div>
              ) : subjects.length === 0 ? (
                <Alert color="info">No subjects found. Create one to get started!</Alert>
              ) : (
                <>
                  <div className="mb-3">
                    <Input
                      type="text"
                      placeholder="Search by name or description..."
                      value={subjectSearch}
                      onChange={(e) => setSubjectSearch(e.target.value)}
                      style={{ maxWidth: '300px' }}
                    />
                  </div>
                  {searchedSubjects.length === 0 ? (
                    <Alert color="warning">
                      <i className="fas fa-search mr-2"></i>
                      No subjects found matching "{subjectSearch}"
                    </Alert>
                  ) : (
                    <>
                      <Table className="align-items-center table-flush" responsive>
                        <thead className="thead-light">
                          <tr>
                            {showCodes && <th>Code</th>}
                            <th>Name</th>
                            <th>Description</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedSubjects.map(subject => (
                            <tr key={subject.subjectId}>
                              {showCodes && <td><strong>{subject.code || '-'}</strong></td>}
                              <td>{subject.name}</td>
                              <td>{subject.description || '-'}</td>
                              <td>
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEditSubject(subject)}
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>{' '}
                                {!subjectHasQuestions(subject.subjectId) && (
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDeleteSubject(subject.subjectId, subject.name)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3">
                        <div className="text-muted small mb-2 mb-sm-0">
                          Showing {((subjectPage - 1) * itemsPerPage) + 1} to {Math.min(subjectPage * itemsPerPage, searchedSubjects.length)} of {searchedSubjects.length} subjects
                        </div>
                        <div className="d-flex align-items-center">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => setSubjectPage(prev => Math.max(1, prev - 1))}
                            disabled={subjectPage === 1 || totalSubjectPages <= 1}
                            className="mr-2"
                          >
                            <i className="fas fa-chevron-left"></i>
                            <span className="d-none d-sm-inline ml-1">Previous</span>
                          </Button>
                          <span className="mx-2 text-nowrap">
                            Page {subjectPage} of {totalSubjectPages || 1}
                          </span>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => setSubjectPage(prev => Math.min(totalSubjectPages, prev + 1))}
                            disabled={subjectPage === totalSubjectPages || totalSubjectPages <= 1}
                            className="ml-2"
                          >
                            <span className="d-none d-sm-inline mr-1">Next</span>
                            <i className="fas fa-chevron-right"></i>
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Units Section */}
        <Col lg="12">
          <Card className="shadow mb-4">
            <CardHeader className="bg-transparent">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">Manage Units</h3>
                </Col>
                <Col className="text-right">
                  <Button color="primary" size="sm" onClick={toggleUnitModal}>
                    <i className="fas fa-plus"></i> Add Unit
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label className="form-control-label">Filter by Subject</Label>
                <Select
                  options={[
                    { value: '', label: 'All Subjects' },
                    ...subjects.map(subject => ({
                      value: subject.subjectId,
                      label: subject.name
                    }))
                  ]}
                  value={
                    selectedSubjectId
                      ? { value: selectedSubjectId, label: subjects.find(s => s.subjectId === selectedSubjectId)?.name }
                      : { value: '', label: 'All Subjects' }
                  }
                  onChange={(option) => setSelectedSubjectId(option.value)}
                  styles={customDropdownWithBorder}
                  placeholder="All Subjects"
                />
              </FormGroup>

              {unitsLoading ? (
                <div className="text-center">Loading units...</div>
              ) : units.length === 0 ? (
                <Alert color="info">No units found. Create one to get started!</Alert>
              ) : (
                <>
                  <div className="mb-3">
                    <Input
                      type="text"
                      placeholder="Search by name, description, or subject..."
                      value={unitSearch}
                      onChange={(e) => {
                        setUnitSearch(e.target.value);
                        setUnitPage(1);
                      }}
                      style={{ maxWidth: '300px' }}
                    />
                  </div>
                  {filteredUnits.length === 0 ? (
                    <Alert color="warning">
                      <i className="fas fa-search mr-2"></i>
                      No units found matching your criteria
                    </Alert>
                  ) : (
                    <>
                      <Table className="align-items-center table-flush" responsive>
                        <thead className="thead-light">
                          <tr>
                            {showCodes && <th>Code</th>}
                            <th>Name</th>
                            <th>Subject</th>
                            <th>Description</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUnits.map(unit => (
                            <tr key={unit.unitId}>
                              {showCodes && <td><strong>{unit.code || '-'}</strong></td>}
                              <td>{unit.name}</td>
                              <td>{getSubjectName(unit.subjectId)}</td>
                              <td>{unit.description || '-'}</td>
                              <td>
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEditUnit(unit)}
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>{' '}
                                {!unitHasQuestions(unit.unitId) && (
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDeleteUnit(unit.unitId, unit.name)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3">
                        <div className="text-muted small mb-2 mb-sm-0">
                          Showing {((unitPage - 1) * itemsPerPage) + 1} to {Math.min(unitPage * itemsPerPage, filteredUnits.length)} of {filteredUnits.length} units
                        </div>
                        <div className="d-flex align-items-center">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => setUnitPage(prev => Math.max(1, prev - 1))}
                            disabled={unitPage === 1 || totalUnitPages <= 1}
                            className="mr-2"
                          >
                            <i className="fas fa-chevron-left"></i>
                            <span className="d-none d-sm-inline ml-1">Previous</span>
                          </Button>
                          <span className="mx-2 text-nowrap">
                            Page {unitPage} of {totalUnitPages || 1}
                          </span>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => setUnitPage(prev => Math.min(totalUnitPages, prev + 1))}
                            disabled={unitPage === totalUnitPages || totalUnitPages <= 1}
                            className="ml-2"
                          >
                            <span className="d-none d-sm-inline mr-1">Next</span>
                            <i className="fas fa-chevron-right"></i>
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Topics Section */}
        <Col lg="12">
          <Card className="shadow">
            <CardHeader className="bg-transparent">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">Manage Topics (Optional)</h3>
                </Col>
                <Col className="text-right">
                  <Button color="primary" size="sm" onClick={toggleTopicModal}>
                    <i className="fas fa-plus"></i> Add Topic
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label className="form-control-label">Filter by Unit</Label>
                <Select
                  options={[
                    { value: '', label: 'All Units' },
                    ...units.map(unit => ({
                      value: unit.unitId,
                      label: `${unit.name} (${getSubjectName(unit.subjectId)})`
                    }))
                  ]}
                  value={
                    selectedUnitId
                      ? { value: selectedUnitId, label: units.find(u => u.unitId === selectedUnitId)?.name }
                      : { value: '', label: 'All Units' }
                  }
                  onChange={(option) => setSelectedUnitId(option.value)}
                  styles={customDropdownWithBorder}
                  placeholder="All Units"
                />
              </FormGroup>

              {topicsLoading ? (
                <div className="text-center">Loading topics...</div>
              ) : topics.length === 0 ? (
                <Alert color="info">No topics found. Create one to get started!</Alert>
              ) : (
                <>
                  <div className="mb-3">
                    <Input
                      type="text"
                      placeholder="Search by name, description, unit, or subject..."
                      value={topicSearch}
                      onChange={(e) => {
                        setTopicSearch(e.target.value);
                        setTopicPage(1);
                      }}
                      style={{ maxWidth: '300px' }}
                    />
                  </div>
                  {filteredTopics.length === 0 ? (
                    <Alert color="warning">
                      <i className="fas fa-search mr-2"></i>
                      No topics found matching your criteria
                    </Alert>
                  ) : (
                    <>
                      <Table className="align-items-center table-flush" responsive>
                        <thead className="thead-light">
                          <tr>
                            {showCodes && <th>Code</th>}
                            <th>Name</th>
                            <th>Unit</th>
                            <th>Subject</th>
                            <th>Description</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedTopics.map(topic => (
                            <tr key={topic.topicId}>
                              {showCodes && <td><strong>{topic.code || '-'}</strong></td>}
                              <td>{topic.name}</td>
                              <td>{getUnitName(topic.unitId)}</td>
                              <td>{getSubjectName(topic.subjectId)}</td>
                              <td>{topic.description || '-'}</td>
                              <td>
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEditTopic(topic)}
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>{' '}
                                {!topicHasQuestions(topic.topicId) && (
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDeleteTopic(topic.topicId, topic.name)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3">
                        <div className="text-muted small mb-2 mb-sm-0">
                          Showing {((topicPage - 1) * itemsPerPage) + 1} to {Math.min(topicPage * itemsPerPage, filteredTopics.length)} of {filteredTopics.length} topics
                        </div>
                        <div className="d-flex align-items-center">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => setTopicPage(prev => Math.max(1, prev - 1))}
                            disabled={topicPage === 1 || totalTopicPages <= 1}
                            className="mr-2"
                          >
                            <i className="fas fa-chevron-left"></i>
                            <span className="d-none d-sm-inline ml-1">Previous</span>
                          </Button>
                          <span className="mx-2 text-nowrap">
                            Page {topicPage} of {totalTopicPages || 1}
                          </span>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => setTopicPage(prev => Math.min(totalTopicPages, prev + 1))}
                            disabled={topicPage === totalTopicPages || totalTopicPages <= 1}
                            className="ml-2"
                          >
                            <span className="d-none d-sm-inline mr-1">Next</span>
                            <i className="fas fa-chevron-right"></i>
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Subject Modal */}
      <Modal isOpen={subjectModal} toggle={toggleSubjectModal} centered>
        <ModalHeader toggle={toggleSubjectModal}>
          {editingSubject ? 'Edit Subject' : 'Add New Subject'}
        </ModalHeader>
        <Form onSubmit={handleSubjectSubmit}>
          <ModalBody>
            {showCodes && (
              <FormGroup>
                <Label for="subjectCode">Code *</Label>
                <Input
                  type="text"
                  id="subjectCode"
                  placeholder="e.g., C"
                  value={subjectForm.code}
                  onChange={(e) => setSubjectForm({ ...subjectForm, code: e.target.value })}
                  required
                />
              </FormGroup>
            )}
            <FormGroup>
              <Label for="subjectName">Name *</Label>
              <Input
                type="text"
                id="subjectName"
                placeholder="e.g., Neurological Rehabilitation"
                value={subjectForm.name}
                onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="subjectDescription">Description</Label>
              <Input
                type="textarea"
                id="subjectDescription"
                placeholder="Optional description"
                value={subjectForm.description}
                onChange={(e) => setSubjectForm({ ...subjectForm, description: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleSubjectModal}>
              <i className="fas fa-times mr-2"></i>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              <i className={`fas fa-${editingSubject ? 'save' : 'plus'} mr-2`}></i>
              {editingSubject ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Unit Modal */}
      <Modal isOpen={unitModal} toggle={toggleUnitModal} centered>
        <ModalHeader toggle={toggleUnitModal}>
          {editingUnit ? 'Edit Unit' : 'Add New Unit'}
        </ModalHeader>
        <Form onSubmit={handleUnitSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>Subject *</Label>
              <Select
                options={subjects.map(subject => ({
                  value: subject.subjectId,
                  label: getDisplayName(subject, activeSpecialty)
                }))}
                value={
                  unitForm.subjectId
                    ? {
                        value: unitForm.subjectId,
                        label: getDisplayName(subjects.find(s => s.subjectId === unitForm.subjectId), activeSpecialty)
                      }
                    : null
                }
                onChange={(option) => setUnitForm({ ...unitForm, subjectId: option?.value || '' })}
                styles={customDropdownWithBorder}
                placeholder="Select a subject"
                isClearable
              />
            </FormGroup>
            {showCodes && (
              <FormGroup>
                <Label for="unitCode">Code *</Label>
                <Input
                  type="text"
                  id="unitCode"
                  placeholder="e.g., C.1"
                  value={unitForm.code}
                  onChange={(e) => setUnitForm({ ...unitForm, code: e.target.value })}
                  required
                />
              </FormGroup>
            )}
            <FormGroup>
              <Label for="unitName">Name *</Label>
              <Input
                type="text"
                id="unitName"
                placeholder="e.g., Brain Injury"
                value={unitForm.name}
                onChange={(e) => setUnitForm({ ...unitForm, name: e.target.value })}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="unitDescription">Description</Label>
              <Input
                type="textarea"
                id="unitDescription"
                placeholder="Optional description"
                value={unitForm.description}
                onChange={(e) => setUnitForm({ ...unitForm, description: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleUnitModal}>
              <i className="fas fa-times mr-2"></i>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              <i className={`fas fa-${editingUnit ? 'save' : 'plus'} mr-2`}></i>
              {editingUnit ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Topic Modal */}
      <Modal isOpen={topicModal} toggle={toggleTopicModal} centered>
        <ModalHeader toggle={toggleTopicModal}>
          {editingTopic ? 'Edit Topic' : 'Add New Topic'}
        </ModalHeader>
        <Form onSubmit={handleTopicSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>Unit *</Label>
              <Select
                options={units.map(unit => ({
                  value: unit.unitId,
                  label: `${getDisplayName(unit, activeSpecialty)} (${getSubjectName(unit.subjectId)})`,
                  subjectId: unit.subjectId
                }))}
                value={
                  topicForm.unitId
                    ? {
                        value: topicForm.unitId,
                        label: getDisplayName(units.find(u => u.unitId === topicForm.unitId), activeSpecialty)
                      }
                    : null
                }
                onChange={(option) => setTopicForm({
                  ...topicForm,
                  unitId: option?.value || '',
                  subjectId: option?.subjectId || ''
                })}
                styles={customDropdownWithBorder}
                placeholder="Select a unit"
                isClearable
              />
            </FormGroup>
            {showCodes && (
              <FormGroup>
                <Label for="topicCode">Code *</Label>
                <Input
                  type="text"
                  id="topicCode"
                  placeholder="e.g., C.1.a"
                  value={topicForm.code}
                  onChange={(e) => setTopicForm({ ...topicForm, code: e.target.value })}
                  required
                />
              </FormGroup>
            )}
            <FormGroup>
              <Label for="topicName">Name *</Label>
              <Input
                type="text"
                id="topicName"
                placeholder="e.g., Traumatic Brain Injury"
                value={topicForm.name}
                onChange={(e) => setTopicForm({ ...topicForm, name: e.target.value })}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="topicDescription">Description</Label>
              <Input
                type="textarea"
                id="topicDescription"
                placeholder="Optional description"
                value={topicForm.description}
                onChange={(e) => setTopicForm({ ...topicForm, description: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleTopicModal}>
              <i className="fas fa-times mr-2"></i>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              <i className={`fas fa-${editingTopic ? 'save' : 'plus'} mr-2`}></i>
              {editingTopic ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageSubjectsUnits;
